import { action, observable } from 'mobx';
import { api, config, setValidate } from '../config';
import {deleteCookie, getCookie, isLocalStorageSupported, promptUser} from '../helpers/helpers';

// Temporarily using default axios instance for translations
import axios from 'axios';

// Dependencies
import LogRocket from 'logrocket';
import browserUpdate from 'browser-update';

// Other stores
import ExamStore from './ExamStore';
import LocationStore from './LocationStore';
import MessageStore from './MessageStore';
import PaymentStore from './PaymentStore';
import ScheduleStore from './ScheduleStore';
import SettingsStore from './SettingsStore';
import moment from "moment";
import ReportStore from "./ReportStore";

let obx = observable({
  locale: 'en',
  logRocketInit: false,
  translation: null,
  translationLoaded: false,
  loadingCalls: [],
  loading: false,
  loadingMessage: null,
  loadingClass: '',
  backgroundLoadingCalls: [],
  backgroundLoading: false,
  authenticated: false,
  userData: null,
  profile: null,
  smarterscheduling: false,
  hasSessionStorage: isLocalStorageSupported(),
  tokenSet: false,
  sessionTimeout: 120,
  timeoutHandler: null,
  timerSet: false,
  getLocalStorage: action(function(data) {
    obx.userData = data;
  }),
  removeLocalStorage: action(function() {
    if(obx.hasSessionStorage) {
      sessionStorage.removeItem('registerData');
      sessionStorage.removeItem('userData');
    } else {
      deleteCookie('registerData');
      deleteCookie('userData');
    }
  }),
  browserUnsupported: false,
  checkBrowser: action(function(){
    // Check if browser is supported
    const _this = this;

    browserUpdate({
      notify: {
        e:-1,
        i:11,
        f:-1,
        o:-1,
        s:-1,
        c:-1
      },
      reminder: 0,
      onshow: function() {
        obx.browserUnsupported = true;
      }
    });
  }),
  login: action(function(username, password, func){
    obx.startLoading('Loading…', 'Login');

    const payload = {
      username, password
    }

    api.post(`/proctors/login`, payload)
      .then(response => {
        obx.finishLoading('Login');
        obx.userData = response.data;

        if(func) {
          func();
        }

        if(obx.hasSessionStorage) {
          sessionStorage.setItem('token', response.data.authentication.token);
          sessionStorage.setItem('iam', JSON.stringify(response.data.authentication.iam));
          sessionStorage.setItem('userData', JSON.stringify(obx.userData));
          sessionStorage.removeItem('registerData');
        } else {
          document.cookie = `token=${response.data.authentication.token}`;
          document.cookie = `iam=${JSON.stringify(response.data.authentication.iam)}`;
          document.cookie = `userData=${JSON.stringify(obx.userData)}`;
          deleteCookie('registerData');
        }
        setValidate();
      })
      .catch(error => {
        obx.throwError(error)
      });
  }),
  logout: action(function(history) {
    if(obx.hasSessionStorage) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('iam');
      sessionStorage.removeItem('userData');
    } else {
      deleteCookie('token');
      deleteCookie('iam');
    }

    obx.resetStores();

    if(history) {
      history.push('/account/sign-in');
    }
  }),
  resetStores: action(function() {
    // Reset user data
    obx.userData = null;
    obx.profile = null;
    obx.account = null;

    // Reset exam data
    ExamStore.resetExam();

    // Reset location data
    LocationStore.locations = [];
    LocationStore.resetLocation();
    LocationStore.examGroups = [];

    // Reset message data
    MessageStore.threads = [];
    MessageStore.threadMessages = [];
    MessageStore.threadObject = {};

    // Reset payment data
    PaymentStore.balance = 0;
    PaymentStore.paymentSessions = [];
    PaymentStore.paymentHistory = [];

    // Reset schedule data
    ScheduleStore.events = [];
    ScheduleStore.session = {};

    // Reset user settings
    SettingsStore.settings = {};
    SettingsStore.userSettings = null;
  }),
  // Get proctor account data
  account: null,
  getProctorAccount: action(function(sid){
    obx.startLoading('Loading…', 'GetProctorAccount');

    api.get(`/proctors/${sid}`)
      .then(response => {
        obx.account = response.data;
        obx.finishLoading('GetProctorAccount');
      })
      .catch(error => {
        obx.throwError(error);
      })
  }),
  startLoading: action(function(message, request) {
    obx.loading = true;
    obx.loadingMessage = message;
    obx.loadingCalls.push(request)
  }),
  finishLoading: action(function(request) {
    const requestIndex = obx.loadingCalls.indexOf(request);

    if(requestIndex >= 0) {
      obx.loadingCalls.splice(requestIndex, 1);
    } else {
      obx.loadingCalls.length = 0;
    }

    if(obx.loadingCalls.length === 0 || typeof request === 'undefined') {
      obx.loadingClass = 'leave';
      obx.loading = false;

      // Wait for loader to fade out before removing
      setTimeout(function(){
        obx.loadingClass = '';
        obx.loadingMessage = null;
      }, 250);
    }
  }),
  startBackgroundLoading: action(function(request) {
    obx.backgroundLoading = true;
    obx.backgroundLoadingCalls.push(request)
  }),
  finishBackgroundLoading: action(function(request) {
    const requestIndex = obx.backgroundLoadingCalls.indexOf(request);

    if(requestIndex >= 0) {
      obx.backgroundLoadingCalls.splice(requestIndex, 1);
    } else {
      obx.backgroundLoadingCalls.length = 0;
    }

    if(obx.backgroundLoadingCalls.length === 0 || typeof request === 'undefined') {
      obx.backgroundLoading = false;
    }
  }),
  dialog: false,
  featureNotice: false,
  featureNoticeSetting: null,
  featureNoticeToggle: null,
  dialogContent: {
    heading: null,
    body: null,
    button: null,
    code: null,
    label: null
  },
  confirm: false,
  confirmContent: {
    heading: null,
    body: null,
    button: null,
  },
  confirmCallback: null,
  prompt: false,
  promptContent: {
    heading: null,
    body: null,
    options: null,
    button: null
  },
  promptSelectFunc: null,
  promptCallback: null,
  throwError: action(function(error) {
    console.error(error);
    obx.finishLoading();

    if(typeof error.response !== 'undefined') {
      // API errors will return an error object with a response
      // which means we have a message for them
      obx.showError(error.response.data);
      console.error(`Server response logged below.`);
      console.log(error.response);
    } else {
      // If there’s no response with the error, it’s a server
      // connection error (i.e. axios can’t reach the URL)
      obx.showError();
      console.error('Server connection error.');
    }
  }),
  expireWarning: false,
  expireSession: action(function() {
    obx.logout();
    window.location.href = '/session-expired';

  }),
  showExpireWarning: action(function() {
    obx.expireWarning = true;
  }),
  removeExpireWarning: action(function() {
    obx.expireWarning = false;
  }),
  extendSession: action(function(callback) {
    let headers = {};

    if(obx.hasSessionStorage) {
      headers = {
        'token': sessionStorage.getItem('token')
      }
    } else {
      headers = {
        'token': getCookie('token')
      }
    }

    api.get('/check', { headers })
      .then(response => {
        if(callback) {
          callback();
        }
      })
      .catch(error => {
        obx.throwError(error);
      })
  }),
  showDialog: action(function(content) {
    if(content) {
      obx.dialogContent = content;
    }

    obx.dialog = true;

    // Focus the confirmation dialog for screen readers
    setTimeout(function(){
      document.querySelector('.dialog__msg').focus();
    })
  }),
  showFeatureNotice: action(function(content) {
    if(content) {
      obx.dialogContent = content;
    }

    obx.featureNotice = true;

    // Focus the confirmation dialog for screen readers
    setTimeout(function(){
      document.querySelector('.dialog__msg').focus();
    })
  }),
  showError: action(function(data) {

    obx.dialog = true;

    obx.dialogContent.heading = obx.translation.Errors.heading;
    obx.dialogContent.button = obx.translation.Errors.button;

    if(typeof data !== 'undefined') {
      obx.dialogContent.code = data.code;
    }

    // Server error (no data present because API didn’t return anything)
    if(!data || typeof data === 'undefined') {
      obx.dialogContent.body = obx.translation.Errors[1301].message;
    } else {
      // API returned data
      if(typeof obx.translation.Errors[data.code] !== 'undefined') {
        obx.dialogContent.body = obx.translation.Errors[data.code].message;

        if(obx.dialogContent.body.indexOf('%contact us%') > -1) {
          obx.dialogContent.body = obx.dialogContent.body.replace('%contact us%', 'contact us');
          obx.dialogContent.typeForm = true;
        }
      } else {
        console.warn('Error code not present in translation file. Falling back to API response message');
        obx.dialogContent.body = data.message;
      }
    }
  }),
  showConfirm: action(function(msg, func) {
    obx.confirm = true;
    obx.confirmContent.heading = msg.heading;
    obx.confirmContent.body = msg.body;
    obx.confirmContent.button = msg.button;
    obx.confirmCallback = func;
  }),
  exportSessionsCSV: action( function () {
    const msg = {
      heading: "Export to CSV",
      input: "Title",
      button: "Export"
    }
    ReportStore.reportDefinition = {
      "Student": "{% if enrollment.lastName and enrollment.firstName %}{{enrollment.lastName | customEscape}}, {{enrollment.firstName | customEscape}}{% endif %}",
      "Student Email": "{% if enrollment.email %}{{enrollment.email | customEscape}}{% endif %}",
      "Date": "{% if appointmentDate %}{{appointmentDate | time_zone: '"+moment.tz.guess()+"', 'M/D/YYYY [at] h:mma z'}}{% endif %}",
      "Exam": "{% if exam %}{{exam.title | customEscape}}{% endif %}",
      "Exam Type": "{% if exam.type == 'Online' %}Computer Based{% else %}Written{% endif %}",
      "Exam Duration": "{% if examConfiguration %}{{examConfiguration.duration | customEscape}}{% endif %}",
      "Exam Open Date": "{% if examConfiguration %}{{examConfiguration.examOpenDate | time_zone: '"+moment.tz.guess()+"', 'M/D/YYYY [at] h:mma z'}}{% endif %}",
      "Exam Close Date": "{% if examConfiguration %}{{examConfiguration.examCloseDate | time_zone: '"+moment.tz.guess()+"', 'M/D/YYYY [at] h:mma z'}}{% endif %}",
      "Seat": "{% if appointment && appointment.seat %}{{appointment.seat.title | customEscape}}{% endif %}",
      "Incidents": "{% if sessionReportCount %}{{sessionReportCount | customEscape}}{% endif %}",
      "Status": "{% if status %}{{status | customEscape}}{% endif %}",
      "Location Name": "{% if location %}{{ location.name | customEscape}}{% endif %}",
      "Session Type": "{% if location.type.type == 'online' %}Live Virtual{% else %}In Person{% endif %}",
      "Course": "{{course.title | customEscape}}",
      "Course Code": "{% if course %}{{course.code | customEscape}}{% endif %}",
      "Instructor": "{% if course %}{{course.instructor.lastName | customEscape}}, {{course.instructor.firstName | customEscape}}{% endif %}",
      "Instructor Email": "{% if course and course.instructor.email %}{{course.instructor.email | customEscape}}{% endif %}",
      "Course Offering": "{% if course  %}{{course.offering.title | customEscape}}{% endif %}",
    }
    const finishExport = () => {
      const callback = () => obx.showToaster("Exported successfully", 'success');
      ReportStore.exportData(callback)
    }
    promptUser(msg, () => finishExport(),  null, ReportStore.reportExportTitle);

  }),
  showPrompt: action(function(msg, selectFunc, callback, text) {
    obx.prompt = true;
    obx.promptContent.heading = msg.heading;
    obx.promptContent.body = msg.body;
    obx.promptContent.options = msg.options;
    obx.promptContent.button = msg.button;
    obx.promptSelectFunc = selectFunc;
    obx.promptCallback = callback;

    if(msg.input) {
      obx.promptContent.input = msg.input;
    }

    if(text) {
      obx.promptText = text;
    }

    // Focus the prompt dialog for screen readers
    setTimeout(function(){
      const dialogMsg = document.querySelector('.dialog__msg');

      if(dialogMsg) {
        dialogMsg.focus();
      }
    });
  }),
  closeDialog: action(function() {
    obx.dialog = false;
    obx.confirm = false;
    obx.expireWarning = false;
    obx.prompt = false;
    obx.promptContent.options = null;
    obx.featureNotice = false;
    obx.featureNoticeSetting = null;
    obx.featureNoticeToggle = null;
  }),
  confirmFunc: action(function() {
    obx.confirm = false;
    obx.confirmCallback();
  }),
  promptFunc: action(function() {
    obx.prompt = false;
    if(obx.promptCallback) {
      obx.promptCallback();
    }
  }),
  proctorTypeList: [],
  currencyList: [],
  timezoneList: [],
  accountList: [],
  getProctorTypes: action(function() {
    api.get(`/proctors/categories`)
      .then(response => {
        obx.proctorTypeList = response.data.results;
      })
      .catch(error => {
        obx.throwError(error)
      });
  }),
  getTimezones: action(function(callback) {
    api.get(`/timeZones`)
      .then(response => {
        obx.timezoneList = response.data.results;

        if (callback) {
          callback(response.data.results);
        }
      })
      .catch(error => {
        obx.throwError(error)
      });
  }),
  // Current time
  currentTime: null,
  getCurrentTime: action(function(backgroundLoad){
    if(!backgroundLoad) {
      obx.startLoading('Loading…', 'CurrentTime');
    }

    api.get(`/currentTime`)
      .then(response => {
        obx.currentTime = response.data.currentTime;

        if(!backgroundLoad) {
          obx.finishLoading('CurrentTime');
        }
      })
      .catch(error => {
        obx.throwError(error)
      });
  }),
  // Currencies
  getCurrencies: action(function(data) {
    api.get(`/currencies`)
      .then(response => {
        obx.currencyList = response.data.results;
      })
      .catch(error => {
        obx.throwError(error)
      });
  }),
  getAccounts: action(function(data) {
    api.get(`/accounts`)
      .then(response => {
        obx.accountList = response.data.results;
      })
      .catch(error => {
        obx.throwError(error)
      });
  }),
  getTranslation: action(function(locale) {
    obx.translationLoaded = true;

    const timestamp = (new Date()).getTime();

    axios.get(`/json/${locale}.json?t=${timestamp}`)
      .then(response => {
        obx.translation = response.data.content;
      });
  }),
  ldClient: null,
  ldReady: false,
  getProfile: action(function(router) {
    if(!obx.tokenSet) return;
    obx.startLoading('Loading…', 'GetProfile');

    api.get(`/proctors/${obx.userData.proctorAccountSid}`)
      .then(response => {
        obx.profile = response.data;

        if(response.data.forceSetupVerification && router) {
          router.push('/checkup');
        }

        // Set LogRocket identity data
        LogRocket.identify(obx.profile.sid, {
          name: `${obx.profile.lastName}, ${obx.profile.firstName}`,
          email: obx.profile.email,
          sid: obx.profile.sid
        });

        obx.finishLoading('GetProfile');

        // Set up Google Analytics
        let gaTrackingId = config.gaProctor;

        const gaScript = document.createElement('script');
        gaScript.id = 'ga-script';
        gaScript.type = 'text/javascript';
        gaScript.async = true;
        gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`;

        // Append Google Analytics script
        document.body.appendChild(gaScript);

        // Config Google Analtyics
        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        gtag('js', new Date());

        // Add installSid dimension
        gtag('config', gaTrackingId, {
          'custom_map': {'dimension1': 'proctorSid'}
        });

        gtag('event', 'proctorSid', {'proctorSid': obx.userData.proctorAccountSid});

        // Initialize Google Analytics
        gtag('config', gaTrackingId);

        // Create tracker
        const gaDelay = setInterval(function(){
          if(window.ga) {
            clearInterval(gaDelay);
            window.ga('create', gaTrackingId, 'auto');
          }
        }, 100);

        // Set up LaundDarkly client
        let environment = 'develop';

        if(process.env.REACT_APP_ENVIRONMENT === 'production') {
          environment = 'production'
        }

        if(process.env.REACT_APP_ENVIRONMENT === 'stage') {
          environment = 'stage'
        }

        const user = {
          key: obx.userData.proctorAccountSid,
          custom: {
            domain: window.location.hostname,
            environment: environment,
            account: obx.userData.proctorAccountSid,
            user: obx.userData.sid
          }
        }

        obx.ldClient = window.LDClient.initialize(config.ldApiKey, user);

        // Check if SmarterScheduling is enabled
        obx.ldClient.on('ready', function(){
          obx.ldReady = true;

          if(obx.ldClient.variation('proctor-schedules', false)) {
            obx.smarterscheduling = true;
          }
        });

        // Check if written exams are accepted
        // if not, check if a cookie exists
        // noting that we've already told them
        // about written exams
        if(obx.profile.acceptWrittenExams === false) {
          const writtenCookie = getCookie('sp-written');

          if(!writtenCookie && !obx.profile.locked) {
            const content = {
              heading: 'Announcing Written Exams',
              body: '<p>You can now choose to accept written exams.</p><p>Written exam files are provided by instructors and must be printed for the student. After the exam session, the completed exam must be scanned and uploaded for return to the instructor.</p><p>Written exams can be enabled or disabled later from your settings page.</p>',
              label: 'Accept written exams',
              button: 'Okay'
            }

            obx.featureNoticeSetting = obx.profile.acceptWrittenExams;
            obx.featureNoticeToggle = () => {
              SettingsStore.toggleSetting(obx.profile, 'acceptWrittenExams')
            };

            obx.showFeatureNotice(content);

            // Store a cookie so the notice
            // doesn’t keep coming up
            document.cookie = 'sp-written=true';
          }
        }
      })
      .catch(error => {
        obx.throwError(error)
        obx.finishLoading('GetProfile');

      });
  }),
  checkToken: action(function(){
    obx.startLoading('Loading…', 'CheckToken');

    let headers = {};

    if(obx.hasSessionStorage) {
      headers = {
        'token': sessionStorage.getItem('token')
      }
    } else {
      headers = {
        'token': getCookie('token')
      }
    }

    Promise.all([api.get(`/check`, {headers}),api.get(`/permissions`, {headers})])
      .then(arr => {
        // API returns the user sid differently than elsewhere,
        // so create a new property
        if(arr[0].data.proctorAccountUserSid) {
          arr[0].data.sid = arr[0].data.proctorAccountUserSid
        }

        if(obx.hasSessionStorage) {
          sessionStorage.setItem('userData', JSON.stringify(arr[0].data));
          sessionStorage.setItem('iam', JSON.stringify(arr[1].data.iam));
        } else {
          document.cookie = `userData=${JSON.stringify(arr[0].data)}`;
          document.cookie = `iam=${JSON.stringify(arr[1].data.iam)}`;
        }

        obx.userData = arr[0].data;
        obx.iam = arr[1].data;

        obx.getProfile();
        obx.finishLoading('CheckToken');
      })
      .catch(error => {
        obx.throwError(error);
      });
  }),
  unreadCount: 0,
  getUnreadCount: action(function(){
    obx.startLoading('Loading…', 'GetUnreadCount');

    api.get(`/messages/counts?userSid=${obx.userData.sid}`)
      .then(response => {
        obx.unreadCount = parseInt(response.data.unread, 10);
        obx.finishLoading('GetUnreadCount');
      })
      .catch(error => {
        obx.throwError(error)
      });
  }),
  // Get associated installations
  installs: null,
  getInstalls: action(function(callback){
    obx.startLoading('Loading…', 'getInstalls');

    api.get(`/proctors/${obx.userData.proctorAccountSid}/associatedInstalls`)
      .then(response => {
        obx.installs = response.data.results;
        obx.finishLoading('getInstalls');

        if(callback) {
          callback(response.data.results);
        }
      })
      .catch(error => {
        obx.throwError(error)
      });
  }),
  // Notifications
  notification: false,
  notificationContent: null,
  notificationLink: null,
  notificationClass: null,
  showNotification: action(function(content, link) {
    if(content) {
      obx.notificationContent = content;
    }

    if(link) {
      obx.notificationLink = link;
    }

    obx.notification = true;
  }),
  closeNotification: action(function() {
    obx.notification = false;
    obx.notificationContent = null;
    obx.notificationLink = null;
  }),
  showToaster: action(function(content, type) {
    if(content) {
      obx.notificationContent = content;
    }

    if(type === 'success') {
      obx.notificationClass = 'notify--success'
    }
    if(type === 'warning') {
      obx.notificationClass = 'notify--warning'
    }
    if(type === 'error') {
      obx.notificationClass = 'notify--error'
    }

    obx.notification = true;

    // Remove the toaster after 5 seconds
    setTimeout(function(){
      obx.notificationClass += ' notify--closing';
    }, 5000);

    setTimeout(function(){
      obx.closeNotification();
    }, 7500);
  }),
  createNewTimer: action(function (timeInSecond, token) {
  obx.timerSet = true;


    function checkTtl (token) {

      return new Promise((resolve, reject) => {
        const headers = {
          'token': token
        }
        api.get(`/ttl`, {headers})
            .then(response => {
              return resolve(response.data.ttl)
            })
            .catch(error => {

              return resolve(-2)
            });
      })
    }

  setTimeout(function(){
    // Check how much time is left
    checkTtl(token)
        .then(res => {

          if(res <= 120 && res > 10) {
            // If 2 minutes or less remain, show the warning
            obx.showExpireWarning();

            // Also start a new timer to expire the session if the user doesn’t extend
            obx.timeoutHandler = obx.createNewTimer(res + 120, token);
          } else if(res <= 10)  {
            // If the session times out, expire it
            obx.expireSession();
            clearTimeout(obx.timeoutHandler);
          } else {
            // Otherwise, create a new timer with the ttl response
            clearTimeout(obx.timeoutHandler);
            obx.removeExpireWarning();
            obx.timeoutHandler = obx.createNewTimer(res, token);
          }
        })
        .catch(error => {
          console.log(error);
        });
  }, (timeInSecond - 120) * 1000);
}),

// Check how much time is left

  startTokenTimer: action(function (token) {
     if(!obx.timerSet) {
       // Create a new timer for session timeout
       obx.createNewTimer(obx.sessionTimeout / 1000, token);
     }
   })

});
export default obx;



// WEBPACK FOOTER //
// ./src/app/stores/SessionStore.js